import React from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import useBreedDescriptionModal from 'dpl/breeder_search_results/hooks/useBreedDescriptionModal';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { breedDescriptionResourceDefinition } from 'dpl/breeder_search_results/utils/queryDefinitions';
import { breedRelationsResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isMobileUA } from 'dpl/shared/utils';
import SmartLink from 'dpl/components/SmartLink';
import BreedDescriptionCarousel from 'dpl/breeder_search_results/components/BreedDescriptionCarousel';

import { BreedDescriptionResponse } from '../constants/resource_types';
import BreedDescriptionRelatedBreed from './BreedDescriptionRelatedBreed';

const IS_MOBILE = isMobileUA();

export default function BreedDescriptionModal() {
  const { isModalOpen, closeModal } = useBreedDescriptionModal();

  const {
    breedDescription,
    breedId,
    citySlug,
    preappliedFilters = {}
  } = useSelector((state: any) => state.server);

  const {
    name,
    traits,
    breed_type: breedType,
    color_breeder_waitlist_preference_descriptions:
      colorBreederWaitlistPreferenceDescriptions,
    size_breeder_waitlist_preference_descriptions:
      sizeBreederWaitlistPreferenceDescriptions,
    learning_center_purebreds_vs_crossbred_url:
      learningCenterPurebredsVsCrossbredUrl,
    results_cover_photos: resultsCoverPhotos
  } = useResourceQuery<BreedDescriptionResponse>(
    breedDescriptionResourceDefinition(breedId)
  ).value.data;

  const relatedBreedsData = useResourceQuery(
    breedRelationsResourceDefinition(
      breedId,
      citySlug,
      preappliedFilters.stateSlug
    )
  ).value.data;

  const hasMultipleBreederWaitlistPreferences =
    colorBreederWaitlistPreferenceDescriptions?.length > 0 &&
    sizeBreederWaitlistPreferenceDescriptions?.length > 0;

  if (isModalOpen) {
    return (
      <MiniOverlayModal
        className="BreederSearchResultsBreedDescriptionModal"
        contentClassName="bg-white br3"
        onCloseClick={closeModal}
      >
        <div className="ph10 pb10 pt16 pt8-lg">
          <span className="f2 f3-sm mb2">About the breed</span>
          <span className="b db f5 f6-sm mb6">{name}</span>
          <div className="slim-gutters mb6 relative br3 overflow-hidden">
            {resultsCoverPhotos?.length && (
              <BreedDescriptionCarousel
                images={resultsCoverPhotos}
                smartImageProps={{
                  Tag: 'div',
                  className:
                    'mr4 bg-center cover w-100 min-w-100 mw-100 h-100 br3',
                  crop: true,
                  loadingClass: 'bg-light-gray',
                  height: IS_MOBILE ? '218px' : '346px',
                  width: IS_MOBILE ? '303px' : '528px'
                }}
              />
            )}
          </div>
          <p
            className="f2 f3-sm mb6"
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: breedDescription }}
          />
          {traits && (
            <div className="mb4">
              <span className="flex f2 f3-sm mb2 fw-medium">
                Breed attributes
              </span>
              <ul className="flex-wrap f2 mb2 gray flex">
                <li
                  key={breedType}
                  className="pv2 ph3 mr2 mb2 dark-gray b--light-gray ba br3 nowrap"
                >
                  <a
                    href={learningCenterPurebredsVsCrossbredUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {breedType}
                  </a>
                </li>
                {traits.map((trait: any, idx: number) => (
                  <li
                    key={idx}
                    className="pv2 ph3 mr2 mb2 dark-gray b--light-gray ba br3 nowrap"
                  >
                    {trait}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div
            className={classnames({
              row: hasMultipleBreederWaitlistPreferences
            })}
          >
            {colorBreederWaitlistPreferenceDescriptions?.length > 0 && (
              <div
                className={classnames('mb6 f2 f3-sm', {
                  'col-sm-6': hasMultipleBreederWaitlistPreferences
                })}
              >
                <span className="flex mb2 fw-medium">Breed colors</span>
                <p className="mb2">
                  {colorBreederWaitlistPreferenceDescriptions.join(', ')}
                </p>
              </div>
            )}
            {sizeBreederWaitlistPreferenceDescriptions?.length > 0 && (
              <div
                className={classnames('mb6 f2 f3-sm', {
                  'col-sm-6': hasMultipleBreederWaitlistPreferences
                })}
              >
                <span className="flex mb2 fw-medium">Breed sizes</span>
                <ul className="flex flex-wrap mb2">
                  {sizeBreederWaitlistPreferenceDescriptions.map(
                    (preference: any, idx: number) => (
                      <li
                        className="flex"
                        key={idx}
                        data-test-id={`BreedDescriptionModalSize--${preference.description}`}
                      >
                        <SmartLink
                          key={idx}
                          to={preference.url}
                          className="stone-700"
                        >
                          {preference.description}
                        </SmartLink>
                        {idx <
                          sizeBreederWaitlistPreferenceDescriptions.length -
                            1 && <div>,&nbsp;</div>}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>
          {relatedBreedsData?.length > 0 && (
            <div className="mb6">
              <span className="f2 f3-sm fw-medium">Related breeds</span>
              <ul className="flex flex-wrap">
                {relatedBreedsData
                  .slice(0, 4)
                  .map((breed: any, idx: number) => (
                    <li key={idx}>
                      <BreedDescriptionRelatedBreed key={idx} breed={breed} />
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {IS_MOBILE && (
            <button
              className="pv4 ba br3 f2 tc fw-medium white bg-blue-500 w-100"
              type="button"
              onClick={closeModal}
            >
              Back to results
            </button>
          )}
        </div>
      </MiniOverlayModal>
    );
  }
  return null;
}
